import { useState } from 'react';
import { Button } from '../ui/Button';
import { ImportPreview } from '../../services/bookingImport';

interface BookingImportPreviewProps {
  preview: ImportPreview;
  onImport: (conflictResolution: 'skip' | 'update' | 'sum') => void;
  onCancel: () => void;
  importing: boolean;
}

export const BookingImportPreview = ({
  preview,
  onImport,
  onCancel,
  importing,
}: BookingImportPreviewProps) => {
  const [conflictResolution, setConflictResolution] = useState<'skip' | 'update' | 'sum'>('skip');
  const [expandedProperty, setExpandedProperty] = useState<string | null>(null);

  const MONTH_NAMES = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  return (
    <div className="space-y-6">
      {/* Resumen general */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg border border-blue-200">
        <h3 className="text-lg font-semibold text-blue-900 mb-4">
          📊 Resumen de Importación
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <p className="text-sm text-blue-700">Propiedades</p>
            <p className="text-2xl font-bold text-blue-900">{preview.summary.totalProperties}</p>
          </div>
          <div>
            <p className="text-sm text-blue-700">Meses</p>
            <p className="text-2xl font-bold text-blue-900">{preview.summary.totalMonths}</p>
          </div>
          <div>
            <p className="text-sm text-blue-700">Reservas</p>
            <p className="text-2xl font-bold text-blue-900">{preview.summary.totalReservations}</p>
          </div>
          <div>
            <p className="text-sm text-blue-700">Facturación Total</p>
            <p className="text-2xl font-bold text-blue-900">
              {preview.summary.totalRevenue.toLocaleString('es-ES', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}€
            </p>
          </div>
        </div>
      </div>

      {/* Advertencias */}
      {preview.warnings.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-yellow-900 mb-2">
            ⚠️ Advertencias ({preview.warnings.length})
          </h4>
          <ul className="text-sm text-yellow-800 space-y-1">
            {preview.warnings.map((warning, idx) => (
              <li key={idx}>• {warning}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Conflictos */}
      {preview.conflicts.length > 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-orange-900 mb-3">
            🔄 Conflictos Detectados ({preview.conflicts.length})
          </h4>
          <p className="text-sm text-orange-800 mb-3">
            Los siguientes meses ya tienen datos en la base de datos:
          </p>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {preview.conflicts.map((conflict, idx) => (
              <div key={idx} className="bg-white p-3 rounded border border-orange-200">
                <p className="font-medium text-sm text-gray-900">
                  {conflict.propertyName} - {MONTH_NAMES[conflict.month - 1]} {conflict.year}
                </p>
                <div className="grid grid-cols-2 gap-4 mt-2 text-xs">
                  <div>
                    <p className="text-gray-600">Actual en BD:</p>
                    <p className="font-medium">
                      {conflict.existingData.revenue.toFixed(2)}€ | 
                      {conflict.existingData.reservations} reservas
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-600">Nuevo del Excel:</p>
                    <p className="font-medium text-orange-700">
                      {conflict.newData.revenue.toFixed(2)}€ | 
                      {conflict.newData.reservations} reservas
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Opciones de resolución de conflictos */}
          <div className="mt-4 pt-4 border-t border-orange-200">
            <p className="text-sm font-medium text-orange-900 mb-2">
              ¿Cómo resolver los conflictos?
            </p>
            <div className="space-y-2">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  name="conflict-resolution"
                  value="skip"
                  checked={conflictResolution === 'skip'}
                  onChange={() => setConflictResolution('skip')}
                  className="text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">
                  <strong>Omitir:</strong> Mantener datos actuales, solo importar nuevos
                </span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  name="conflict-resolution"
                  value="update"
                  checked={conflictResolution === 'update'}
                  onChange={() => setConflictResolution('update')}
                  className="text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">
                  <strong>Actualizar:</strong> Reemplazar datos actuales con los del Excel
                </span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  name="conflict-resolution"
                  value="sum"
                  checked={conflictResolution === 'sum'}
                  onChange={() => setConflictResolution('sum')}
                  className="text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">
                  <strong>Sumar:</strong> Sumar los valores del Excel a los actuales
                </span>
              </label>
            </div>
          </div>
        </div>
      )}

      {/* Detalle por propiedad */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
          <h4 className="font-semibold text-gray-900">
            Detalle por Propiedad ({preview.propertySheets.length})
          </h4>
        </div>
        <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
          {preview.propertySheets.map((sheet, idx) => (
            <div key={idx} className="p-4">
              <button
                onClick={() => setExpandedProperty(
                  expandedProperty === sheet.sheetName ? null : sheet.sheetName
                )}
                className="w-full flex items-center justify-between hover:bg-gray-50 p-2 rounded transition-colors"
              >
                <div className="flex items-center space-x-3">
                  <span className="text-2xl">
                    {expandedProperty === sheet.sheetName ? '📂' : '📁'}
                  </span>
                  <div className="text-left">
                    <p className="font-medium text-gray-900">
                      {sheet.propertyName}
                      {!sheet.propertyId && (
                        <span className="ml-2 text-xs text-red-600 font-normal">
                          (No encontrada en BD)
                        </span>
                      )}
                    </p>
                    <p className="text-sm text-gray-600">
                      {sheet.monthlyData.length} meses • {sheet.totalReservations} reservas • 
                      {sheet.totalRevenue.toFixed(2)}€
                    </p>
                  </div>
                </div>
                <span className="text-gray-400">
                  {expandedProperty === sheet.sheetName ? '▼' : '▶'}
                </span>
              </button>

              {/* Detalle expandido */}
              {expandedProperty === sheet.sheetName && (
                <div className="mt-3 ml-10 space-y-2">
                  {sheet.monthlyData.map((month, monthIdx) => (
                    <div
                      key={monthIdx}
                      className="bg-gray-50 p-3 rounded border border-gray-200"
                    >
                      <div className="flex justify-between items-start mb-2">
                        <p className="font-medium text-sm">
                          {MONTH_NAMES[month.month - 1]} {month.year}
                        </p>
                        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                          {month.reservations} reservas
                        </span>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                        <div>
                          <p className="text-gray-600">Facturación</p>
                          <p className="font-medium">{month.revenue.toFixed(2)}€</p>
                        </div>
                        <div>
                          <p className="text-gray-600">Limpieza</p>
                          <p className="font-medium">{month.cleaningCosts.toFixed(2)}€</p>
                        </div>
                        <div>
                          <p className="text-gray-600">Inmohost</p>
                          <p className="font-medium">{month.inmohostCosts.toFixed(2)}€</p>
                        </div>
                        <div>
                          <p className="text-gray-600">Beneficio</p>
                          <p className="font-medium text-green-700">{month.profit.toFixed(2)}€</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Botones de acción */}
      <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
        <Button
          onClick={onCancel}
          variant="secondary"
          disabled={importing}
        >
          Cancelar
        </Button>
        <Button
          onClick={() => onImport(conflictResolution)}
          variant="primary"
          loading={importing}
          disabled={preview.propertySheets.every(ps => !ps.propertyId)}
        >
          {importing ? 'Importando...' : 'Confirmar Importación'}
        </Button>
      </div>

      {/* Advertencia final */}
      {preview.propertySheets.every(ps => !ps.propertyId) && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-800">
            ⚠️ No se puede importar porque ninguna propiedad fue encontrada en la base de datos.
            Verifica que los nombres de las propiedades coincidan.
          </p>
        </div>
      )}
    </div>
  );
};
